// ========================================
// KEY CREATE / EDIT MODAL
// Handles both creating new LiteLLM keys and editing existing ones.
// Essential fields always visible; Advanced fields in collapsible section.
// ========================================

import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { createKey, updateKey } from '../services/api';
import Modal from './shared/Modal';
import Button from './shared/Button';

// Duration options shared across fields
const DURATION_OPTIONS = [
    { label: 'Never', value: '' },
    { label: '1 day', value: '1d' },
    { label: '7 days', value: '7d' },
    { label: '30 days', value: '30d' },
    { label: '90 days', value: '90d' },
    { label: '1 year', value: '365d' }
];

const BUDGET_DURATION_OPTIONS = [
    { label: 'Never resets', value: '' },
    { label: 'Daily', value: '1d' },
    { label: 'Weekly', value: '7d' },
    { label: 'Monthly (30 days)', value: '30d' },
    { label: 'Quarterly (90 days)', value: '90d' }
];

// Model name mapping for friendly display
const MODEL_DISPLAY_NAMES = {
    // Anthropic Claude models
    'claude-sonnet-4-6': 'Claude Sonnet 4.6',
    'claude-sonnet-4-5-20250929': 'Claude Sonnet 4.5',
    'claude-opus-4-1-20250805': 'Claude Opus 4.1',
    'claude-sonnet-3-5': 'Claude Sonnet 3.5',
    'claude-opus-3': 'Claude Opus 3',
    'claude-haiku-3-5': 'Claude Haiku 3.5',
    // OpenAI models
    'gpt-5.2': 'GPT-5.2',
    'gpt-5.1': 'GPT-5.1',
    'gpt-5': 'GPT-5',
    'gpt-5-nano': 'GPT-5 Nano',
    'gpt-5-mini': 'GPT-5 Mini',
    'gpt-4.1-nano': 'GPT-4.1 Nano',
    'gpt-4.1-mini': 'GPT-4.1 Mini',
    'gpt-4o': 'GPT-4o',
    'gpt-4o-mini': 'GPT-4o Mini',
    'gpt-4': 'GPT-4',
    'gpt-4-turbo': 'GPT-4 Turbo',
    'gpt-3.5-turbo': 'GPT-3.5 Turbo',
    'o3': 'O3',
    // Google Gemini models
    'gemini-3-pro-preview': 'Gemini 3 Pro Preview',
    'gemini-3-flash-preview': 'Gemini 3 Flash Preview',
    'gemini-2.5-flash': 'Gemini 2.5 Flash',
};

// Format model name to user-friendly display name
const formatModelDisplayName = (modelName) => {
    if (!modelName) return modelName;

    // Check for direct mapping
    if (MODEL_DISPLAY_NAMES[modelName]) {
        return MODEL_DISPLAY_NAMES[modelName];
    }

    // Try without version suffix
    const baseModel = modelName.replace(/-v\d+:\d+$/, '').replace(/-\d{8}$/, '');
    if (MODEL_DISPLAY_NAMES[baseModel]) {
        return MODEL_DISPLAY_NAMES[baseModel];
    }

    // Return original if no mapping found
    return modelName;
};

// Shared input style
const inputStyle = {
    width: '100%',
    padding: '0.55rem 0.75rem',
    borderRadius: '6px',
    border: '1px solid var(--border)',
    backgroundColor: 'var(--surface)',
    color: 'var(--fg-primary)',
    fontSize: '0.9rem',
    outline: 'none',
    boxSizing: 'border-box'
};

const labelStyle = {
    display: 'block',
    marginBottom: '0.35rem',
    fontWeight: '500',
    color: 'var(--fg-secondary)',
    fontSize: '0.85rem'
};

const hintStyle = {
    fontSize: '0.75rem',
    color: 'var(--muted)',
    marginTop: '0.25rem'
};

const sectionStyle = {
    backgroundColor: 'var(--surface)',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1rem'
};

const sectionTitleStyle = {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: 'var(--muted)',
    fontWeight: '600',
    marginBottom: '0.75rem'
};

const rowStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.75rem',
    marginBottom: '0.75rem'
};

/**
 * TagInput - simple chip-style tag input
 */
const TagInput = ({ tags, onChange }) => {
    const [inputValue, setInputValue] = useState('');

    const addTag = () => {
        const trimmed = inputValue.trim();
        if (trimmed && !tags.includes(trimmed)) {
            onChange([...tags, trimmed]);
        }
        setInputValue('');
    };

    const removeTag = (tag) => {
        onChange(tags.filter(t => t !== tag));
    };

    return (
        <div>
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.35rem',
                marginBottom: '0.5rem',
                minHeight: '28px'
            }}>
                {tags.map(tag => (
                    <span key={tag} style={{
                        backgroundColor: 'var(--surface)',
                        border: '1px solid var(--border)',
                        borderRadius: '12px',
                        padding: '0.2rem 0.6rem',
                        fontSize: '0.8rem',
                        color: 'var(--fg-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.35rem'
                    }}>
                        {tag}
                        <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            style={{
                                background: 'none', border: 'none',
                                color: 'var(--critical)', cursor: 'pointer',
                                padding: 0, lineHeight: 1, fontSize: '0.9rem'
                            }}
                        >×</button>
                    </span>
                ))}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                    type="text"
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                    placeholder="Type and press Enter"
                    style={{ ...inputStyle, flex: 1 }}
                />
                <button
                    type="button"
                    onClick={addTag}
                    style={{
                        padding: '0.55rem 0.75rem',
                        backgroundColor: 'var(--border)',
                        color: 'var(--fg-primary)',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                    }}
                >Add</button>
            </div>
        </div>
    );
};

/**
 * MetadataEditor - key-value pair builder (no raw JSON required)
 */
const MetadataEditor = ({ metadata, onChange }) => {
    const [newKey, setNewKey] = useState('');
    const [newValue, setNewValue] = useState('');

    const entries = Object.entries(metadata);

    const addEntry = () => {
        const k = newKey.trim();
        const v = newValue.trim();
        if (k) {
            onChange({ ...metadata, [k]: v });
            setNewKey('');
            setNewValue('');
        }
    };

    const removeEntry = (k) => {
        const updated = { ...metadata };
        delete updated[k];
        onChange(updated);
    };

    return (
        <div>
            {entries.length > 0 && (
                <div style={{ marginBottom: '0.5rem' }}>
                    {entries.map(([k, v]) => (
                        <div key={k} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.35rem 0.5rem',
                            backgroundColor: 'var(--surface)',
                            borderRadius: '4px',
                            marginBottom: '0.35rem',
                            fontSize: '0.82rem'
                        }}>
                            <span>
                                <span style={{ color: 'var(--border)', fontWeight: '500' }}>{k}</span>
                                <span style={{ color: 'var(--muted)' }}>{': '}</span>
                                <span style={{ color: 'var(--fg-primary)' }}>{v}</span>
                            </span>
                            <button
                                type="button"
                                onClick={() => removeEntry(k)}
                                style={{
                                    background: 'none', border: 'none',
                                    color: 'var(--critical)', cursor: 'pointer',
                                    padding: 0, fontSize: '0.9rem'
                                }}
                            >×</button>
                        </div>
                    ))}
                </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.5rem' }}>
                <input
                    type="text"
                    value={newKey}
                    onChange={e => setNewKey(e.target.value)}
                    placeholder="Key"
                    style={inputStyle}
                />
                <input
                    type="text"
                    value={newValue}
                    onChange={e => setNewValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEntry(); } }}
                    placeholder="Value"
                    style={inputStyle}
                />
                <button
                    type="button"
                    onClick={addEntry}
                    style={{
                        padding: '0.55rem 0.75rem',
                        backgroundColor: 'var(--border)',
                        color: 'var(--fg-primary)',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        whiteSpace: 'nowrap'
                    }}
                >+ Add</button>
            </div>
        </div>
    );
};

/**
 * ModelMultiSelect - checkbox list for selecting models
 */
const ModelMultiSelect = ({ selectedModels, allModels, onChange }) => {
    const [search, setSearch] = useState('');

    // allModels comes from OpenWebUI and has prefixes like "PLUS.gpt-5", "BKK.gpt-4o"
    // We need to extract the actual model names and display them properly
    const modelNames = allModels
        .map(m => {
            let name = m.id || m.model_name || (typeof m === 'string' ? m : null);
            if (!name) return null;

            // Remove OpenWebUI team prefix (e.g., "PLUS.gpt-5" -> "gpt-5")
            const parts = name.split('.');
            if (parts.length > 1 && ['PLUS', 'BKK', 'CNX'].includes(parts[0])) {
                name = parts.slice(1).join('.');
            }

            return name;
        })
        .filter(Boolean)
        .filter(name => !name.toLowerCase().includes('auto-router') && !name.toLowerCase().includes('router'));

    // Remove duplicates (since PLUS.gpt-5, BKK.gpt-5, CNX.gpt-5 all become gpt-5)
    const uniqueModelNames = [...new Set(modelNames)].sort();

    const filtered = uniqueModelNames.filter(m =>
        m.toLowerCase().includes(search.toLowerCase())
    );

    const toggleModel = (name) => {
        if (selectedModels.includes(name)) {
            onChange(selectedModels.filter(m => m !== name));
        } else {
            onChange([...selectedModels, name]);
        }
    };

    return (
        <div>
            <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search models..."
                style={{ ...inputStyle, marginBottom: '0.5rem' }}
            />
            <div style={{
                maxHeight: '160px',
                overflowY: 'auto',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                backgroundColor: 'var(--surface)'
            }}>
                {/* All models option */}
                <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    cursor: 'pointer',
                    borderBottom: '1px solid var(--border)',
                    color: 'var(--border)',
                    fontWeight: '500',
                    fontSize: '0.85rem'
                }}>
                    <input
                        type="checkbox"
                        checked={selectedModels.length === 0}
                        onChange={() => onChange([])}
                    />
                    All models (no restriction)
                </label>
                {filtered.map(name => {
                    const displayName = formatModelDisplayName(name);

                    return (
                        <label key={name} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.4rem 0.75rem',
                            cursor: 'pointer',
                            borderBottom: '1px solid var(--border)',
                            color: 'var(--fg-primary)',
                            fontSize: '0.83rem'
                        }}>
                            <input
                                type="checkbox"
                                checked={selectedModels.includes(name)}
                                onChange={() => toggleModel(name)}
                            />
                            <div style={{ flex: 1, color: 'var(--fg-primary)' }}>
                                {displayName}
                            </div>
                        </label>
                    );
                })}
                {filtered.length === 0 && (
                    <div style={{ padding: '0.75rem', color: 'var(--muted)', fontSize: '0.85rem' }}>
                        No models match
                    </div>
                )}
            </div>
            {selectedModels.length > 0 && (
                <div style={{ ...hintStyle, marginTop: '0.35rem' }}>
                    {selectedModels.length} model{selectedModels.length !== 1 ? 's' : ''} selected
                </div>
            )}
        </div>
    );
};

// ========================================
// MAIN COMPONENT
// ========================================

/**
 * KeyCreateEditModal
 *
 * @param {string} mode - 'create' or 'edit'
 * @param {Object} team - Team object (team_id, team_alias) - initial team selection
 * @param {Array} allTeams - All available teams for dropdown selection
 * @param {Object} [keyData] - Existing key data when mode='edit'
 * @param {Array} allModels - All available models
 * @param {Function} onSuccess - Called with created/updated key data
 * @param {Function} onClose - Called to close modal
 */
const KeyCreateEditModal = ({ mode, team: initialTeam, allTeams = [], keyData = null, allModels = [], onSuccess, onClose }) => {
    const { accounts } = useMsal();
    const currentUserEmail = accounts[0]?.username || accounts[0]?.email || 'unknown@aware.co.th';

    const isEdit = mode === 'edit';

    // Team selection state (only for create mode)
    const [selectedTeam, setSelectedTeam] = useState(initialTeam || { team_id: '__no_team__', team_alias: 'No Team (Unassigned)' });

    // ========================================
    // FORM STATE - initialized from keyData if editing
    // ========================================
    const [keyAlias, setKeyAlias] = useState(keyData?.key_alias || '');

    // Initialize selectedModels:
    // - For new keys: pre-select team's models
    // - For edit: if key has "all-team-models", show team models as selected; otherwise use key's models
    const getInitialModels = () => {
        if (isEdit && keyData?.models) {
            // If editing and key uses "all-team-models", show actual team models for clarity
            if (keyData.models.includes('all-team-models')) {
                return initialTeam?.models || [];
            }
            return keyData.models;
        }
        // For new keys, pre-select selected team's models
        return selectedTeam?.models || [];
    };
    const [selectedModels, setSelectedModels] = useState(getInitialModels());

    // Update selected models when team changes (create mode only)
    useEffect(() => {
        if (!isEdit && selectedTeam) {
            setSelectedModels(selectedTeam.models || []);
        }
    }, [selectedTeam, isEdit]);

    const [maxBudget, setMaxBudget] = useState(keyData?.max_budget != null ? String(keyData.max_budget) : '');
    const [budgetDuration, setBudgetDuration] = useState(keyData?.budget_duration || '');
    const [expiryDuration, setExpiryDuration] = useState(''); // duration is write-only on create; not returned in list
    const [rpmLimit, setRpmLimit] = useState(keyData?.rpm_limit != null ? String(keyData.rpm_limit) : '');
    const [tpmLimit, setTpmLimit] = useState(keyData?.tpm_limit != null ? String(keyData.tpm_limit) : '');

    // Advanced
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [maxParallelRequests, setMaxParallelRequests] = useState(
        keyData?.max_parallel_requests != null ? String(keyData.max_parallel_requests) : ''
    );
    const [tags, setTags] = useState(keyData?.tags || []);
    const [metadata, setMetadata] = useState(keyData?.metadata || {});
    const [blocked, setBlocked] = useState(keyData?.blocked || false);

    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState(null);

    // ========================================
    // SUBMIT
    // ========================================
    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError(null);
        setSaving(true);

        const payload = {
            key_alias: keyAlias.trim() || undefined,
            models: selectedModels,
            max_budget: maxBudget !== '' ? maxBudget : undefined,
            budget_duration: budgetDuration || undefined,
            rpm_limit: rpmLimit !== '' ? rpmLimit : undefined,
            tpm_limit: tpmLimit !== '' ? tpmLimit : undefined,
            max_parallel_requests: maxParallelRequests !== '' ? maxParallelRequests : undefined,
            tags: tags.length > 0 ? tags : undefined,
            metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
            blocked
        };

        // Only include team_id if a real team is selected (not the "No Team" option)
        const teamToUse = isEdit ? initialTeam : selectedTeam;
        if (teamToUse?.team_id && teamToUse.team_id !== '__no_team__') {
            payload.team_id = teamToUse.team_id;
        }

        let result;
        if (isEdit) {
            payload.key = keyData.token || keyData.key_name;
            result = await updateKey(payload, currentUserEmail);
        } else {
            if (expiryDuration) payload.duration = expiryDuration;
            result = await createKey(payload, currentUserEmail);
        }

        if (result.success) {
            onSuccess(result.data);
        } else {
            setFormError(result.error || 'Operation failed. Please try again.');
        }

        setSaving(false);
    };

    // ========================================
    // RENDER
    // ========================================
    const title = isEdit
        ? `Edit Key: ${keyData?.key_alias || 'Unnamed'}`
        : 'Create New API Key';

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={title}
            maxWidth="620px"
            zIndex={1200}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSubmit} loading={saving}>
                        {isEdit ? 'Save Changes' : 'Create Key'}
                    </Button>
                </>
            }
        >
            <form onSubmit={handleSubmit}>
                {formError && (
                    <div style={{
                        padding: '0.75rem 1rem',
                        backgroundColor: 'var(--critical-bg)',
                        border: '1px solid var(--critical)',
                        borderRadius: '6px',
                        color: 'var(--critical)',
                        fontSize: '0.9rem',
                        marginBottom: '1rem'
                    }}>
                        {formError}
                    </div>
                )}

                {/* Team selector (create mode) or display (edit mode) */}
                {isEdit ? (
                    // Edit mode - show team as read-only
                    <div style={{
                        display: 'flex',
                        gap: '0.5rem',
                        marginBottom: '1rem',
                        padding: '0.5rem 0.75rem',
                        backgroundColor: 'var(--surface)',
                        borderRadius: '6px',
                        fontSize: '0.82rem',
                        color: 'var(--muted)'
                    }}>
                        <span>Team:</span>
                        {initialTeam?.team_id === '__no_team__' ? (
                            <span style={{ color: 'var(--warning)', fontWeight: '500' }}>
                                Unassigned (No Team)
                            </span>
                        ) : (
                            <>
                                <span style={{ color: 'var(--fg-primary)', fontWeight: '500' }}>
                                    {initialTeam?.team_alias || initialTeam?.team_id}
                                </span>
                                <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                    {initialTeam?.team_id}
                                </span>
                            </>
                        )}
                    </div>
                ) : (
                    // Create mode - show team selector
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={labelStyle}>Team Assignment</label>
                        <select
                            value={selectedTeam?.team_id || '__no_team__'}
                            onChange={(e) => {
                                if (e.target.value === '__no_team__') {
                                    setSelectedTeam({ team_id: '__no_team__', team_alias: 'No Team (Unassigned)' });
                                } else {
                                    const team = allTeams.find(t => t.team_id === e.target.value);
                                    if (team) setSelectedTeam(team);
                                }
                            }}
                            style={inputStyle}
                        >
                            <option value="__no_team__">No Team (Unassigned)</option>
                            {allTeams.map(t => (
                                <option key={t.team_id} value={t.team_id}>
                                    {t.team_alias || t.team_id}
                                </option>
                            ))}
                        </select>
                        <div style={hintStyle}>
                            Assign this key to a team, or leave unassigned. Team assignment determines budget limits and model access.
                        </div>
                    </div>
                )}

                {/* ── ESSENTIAL FIELDS ── */}
                <div style={sectionStyle}>
                    <div style={sectionTitleStyle}>Essential</div>

                    {/* Key Alias */}
                    <div style={{ marginBottom: '0.75rem' }}>
                        <label style={labelStyle}>Key Alias (optional)</label>
                        <input
                            type="text"
                            value={keyAlias}
                            onChange={e => setKeyAlias(e.target.value)}
                            placeholder="e.g. Production API key, Marketing bot"
                            style={inputStyle}
                            maxLength={100}
                        />
                        <div style={hintStyle}>
                            A friendly name to identify this key. Shown in the key list.
                        </div>
                    </div>

                    {/* Models */}
                    <div style={{ marginBottom: '0.75rem' }}>
                        <label style={labelStyle}>Allowed Models (Key-Level Restriction)</label>
                        <ModelMultiSelect
                            selectedModels={selectedModels}
                            allModels={allModels}
                            onChange={setSelectedModels}
                        />
                        <div style={hintStyle}>
                            Pre-selected with team's models. You can restrict to a subset or leave empty to inherit all team models.
                        </div>
                    </div>

                    {/* Budget */}
                    <div style={rowStyle}>
                        <div>
                            <label style={labelStyle}>Max Budget (USD) - Key Level</label>
                            <input
                                type="number"
                                value={maxBudget}
                                onChange={e => setMaxBudget(e.target.value)}
                                placeholder="Leave blank for unlimited"
                                min="0"
                                step="0.01"
                                style={inputStyle}
                            />
                            <div style={hintStyle}>
                                Independent of team budget. Resets based on schedule below.
                            </div>
                        </div>
                        <div>
                            <label style={labelStyle}>Budget Resets - Key Level</label>
                            <select
                                value={budgetDuration}
                                onChange={e => setBudgetDuration(e.target.value)}
                                style={{ ...inputStyle, cursor: 'pointer' }}
                            >
                                {BUDGET_DURATION_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            <div style={hintStyle}>
                                How often the key's budget spend resets to $0.
                            </div>
                        </div>
                    </div>

                    {/* Expiry - create only */}
                    {!isEdit && (
                        <div style={{ marginBottom: '0.75rem' }}>
                            <label style={labelStyle}>Key Expires After</label>
                            <select
                                value={expiryDuration}
                                onChange={e => setExpiryDuration(e.target.value)}
                                style={{ ...inputStyle, cursor: 'pointer' }}
                            >
                                {DURATION_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            <div style={hintStyle}>
                                After this period the key will no longer work.
                            </div>
                        </div>
                    )}

                    {/* Rate limits */}
                    <div style={rowStyle}>
                        <div>
                            <label style={labelStyle}>RPM Limit</label>
                            <input
                                type="number"
                                value={rpmLimit}
                                onChange={e => setRpmLimit(e.target.value)}
                                placeholder="Requests/min — blank = unlimited"
                                min="0"
                                style={inputStyle}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>TPM Limit</label>
                            <input
                                type="number"
                                value={tpmLimit}
                                onChange={e => setTpmLimit(e.target.value)}
                                placeholder="Tokens/min — blank = unlimited"
                                min="0"
                                style={inputStyle}
                            />
                        </div>
                    </div>
                </div>

                {/* ── ADVANCED TOGGLE ── */}
                <button
                    type="button"
                    onClick={() => setShowAdvanced(v => !v)}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--border)',
                        cursor: 'pointer',
                        fontSize: '0.88rem',
                        padding: '0 0 0.75rem 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.35rem'
                    }}
                >
                    <span style={{ fontSize: '0.7rem' }}>{showAdvanced ? '▼' : '▶'}</span>
                    {showAdvanced ? 'Hide advanced options' : 'Show advanced options'}
                </button>

                {/* ── ADVANCED FIELDS ── */}
                {showAdvanced && (
                    <div style={sectionStyle}>
                        <div style={sectionTitleStyle}>Advanced</div>

                        {/* Max Parallel Requests */}
                        <div style={{ marginBottom: '0.75rem' }}>
                            <label style={labelStyle}>Max Parallel Requests</label>
                            <input
                                type="number"
                                value={maxParallelRequests}
                                onChange={e => setMaxParallelRequests(e.target.value)}
                                placeholder="Blank = unlimited"
                                min="0"
                                style={inputStyle}
                            />
                            <div style={hintStyle}>
                                Raises HTTP 429 if this key exceeds N simultaneous requests.
                            </div>
                        </div>

                        {/* Tags */}
                        <div style={{ marginBottom: '0.75rem' }}>
                            <label style={labelStyle}>Tags</label>
                            <TagInput tags={tags} onChange={setTags} />
                            <div style={hintStyle}>
                                Used for tracking spend and routing. Press Enter or click Add.
                            </div>
                        </div>

                        {/* Metadata */}
                        <div style={{ marginBottom: '0.75rem' }}>
                            <label style={labelStyle}>Metadata</label>
                            <MetadataEditor metadata={metadata} onChange={setMetadata} />
                            <div style={hintStyle}>
                                Arbitrary key-value data stored with the key (e.g. team, app, owner).
                            </div>
                        </div>

                        {/* Blocked */}
                        <div>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                cursor: 'pointer',
                                userSelect: 'none'
                            }}>
                                <input
                                    type="checkbox"
                                    checked={blocked}
                                    onChange={e => setBlocked(e.target.checked)}
                                    style={{ width: '16px', height: '16px' }}
                                />
                                <span style={{ color: 'var(--fg-secondary)', fontSize: '0.88rem' }}>
                                    Create as blocked
                                </span>
                            </label>
                            <div style={{ ...hintStyle, marginLeft: '24px' }}>
                                Key will be created but unable to make requests until unblocked.
                            </div>
                        </div>
                    </div>
                )}
            </form>
        </Modal>
    );
};

export default KeyCreateEditModal;

